import AppConfig from '@/AppConfig';
import {
  AgentJourneyEnded,
  AgentSessionBar,
  AgentSessionExpiryWarning,
} from '@/components/RetailHealthInsurance/AgentSession';
import LoadingScreen from '@/components/shared/LoadingScreen';
import { HealthInsuranceContextProvider } from '@/context/HealthInsuranceContext';
import { HealthInsuranceFormsProvider } from '@/context/HealthInsuranceFormsContext';
import useRhiVersion from '@/hooks/useRhiVersion';
import {
  AGENT_SESSION_ENDED_EVENT,
  getRedeemFailure,
  isAgentLaunch,
  isAgentSessionEnded,
} from '@/utils/agentSession';
import {
  HEALTH_INSURANCE_BASE_PATH,
  RHI_V2_AGENT_PATH,
  rhiV2Paths,
} from '@/utils/rhiV2/journey';
import { isRhiRouteOpenTo } from '@/utils/rhiRouteOwners';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

const AGENT_BLOCKED_PATHS = [
  '/retail/health-insurance',
  '/retail/health-insurance/recommendation',
  '/retail/health-insurance/select-member',
  '/retail/health-insurance/plans-benefits',
];

const AGENT_CUSTOMER_ONLY_PATH =
  /^\/retail\/health-insurance\/(?:payment|policy)\/([^/]+)\/?$/;

const BaseHealthInsurance = () => {
  const { pathname } = useLocation();
  const [hasSessionEnded, setHasSessionEnded] = useState(isAgentSessionEnded);
  const rhiVersion = useRhiVersion();

  useEffect(() => {
    document.title = AppConfig.RETAIL_HEALTH_INSURANCE_APP_NAME;
    return () => {
      document.title = 'Claims';
    };
  }, []);

  useEffect(() => {
    if (!isAgentLaunch()) {
      return;
    }

    const handleSessionEnded = () => setHasSessionEnded(true);
    window.addEventListener(AGENT_SESSION_ENDED_EVENT, handleSessionEnded);

    return () => {
      window.removeEventListener(AGENT_SESSION_ENDED_EVENT, handleSessionEnded);
    };
  }, []);

  const cannotContinueAsAgent =
    isAgentLaunch() &&
    (hasSessionEnded || !!getRedeemFailure());

  if (cannotContinueAsAgent) {
    return <AgentJourneyEnded />;
  }

  const normalisedPathname =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

  if (isAgentLaunch() && AGENT_BLOCKED_PATHS.includes(normalisedPathname)) {
    return <Navigate to={RHI_V2_AGENT_PATH} replace />;
  }

  const customerOnlyMatch = isAgentLaunch()
    ? AGENT_CUSTOMER_ONLY_PATH.exec(pathname)
    : null;

  if (customerOnlyMatch) {
    return (
      <Navigate
        to={rhiV2Paths.proposalReview(customerOnlyMatch[1])}
        replace
      />
    );
  }

  if (!rhiVersion) {
    return <LoadingScreen />;
  }

  if (!isRhiRouteOpenTo(pathname, rhiVersion)) {
    return <Navigate to={HEALTH_INSURANCE_BASE_PATH} replace />;
  }

  return (
    <HealthInsuranceContextProvider>
      <HealthInsuranceFormsProvider>
        <AgentSessionBar />
        <AgentSessionExpiryWarning />
        <Outlet />
      </HealthInsuranceFormsProvider>
    </HealthInsuranceContextProvider>
  );
};

export default BaseHealthInsurance;
